import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Icon from '../components/Icons';
import { fmtDate, formatCharge, serviceOf } from '../data/common';
import { bookingStatus, statusLabel, useBookings } from './BookingsContext';
import { AccountNav } from './BookingsListPage';
import { FareBreakdown, TravellerTable } from './FlowParts';
import DocumentLink from '../documents/DocumentLink';
import AmendmentPanel from './AmendmentPanel';

const payLabel = { upi: 'UPI', card: 'Credit Card', international: 'International Card', debit: 'Debit Card', netbanking: 'Net Banking', nb: 'Net Banking', emi: 'No-cost EMI' };

/** Booking details with cancellation, shared by all modules. */
export default function BookingDetailsPage({ module, Summary, refLabel = 'Booking reference', refund, policy = [], extraDetails, allowCancel = true }) {
  const { bookingId } = useParams();
  const { getBooking, cancelBooking } = useBookings();
  const [confirming, setConfirming] = useState(false);
  const booking = getBooking(bookingId);
  const s = serviceOf(module);

  if (!booking || booking.module !== module) {
    return (
      <div className="container account">
        <AccountNav />
        <div className="account-main panel empty">
          <span className="empty-icon"><Icon name="compass" size={32} /></span>
          <h3>Booking not found</h3>
          <Link to={`${s.path}/bookings`} className="btn btn-primary">Back to {s.label.toLowerCase()} bookings</Link>
        </div>
      </div>
    );
  }

  const st = bookingStatus(booking);
  const estimate = st === 'upcoming' ? refund(booking) : null;

  return (
    <div className="container account">
      <AccountNav />
      <div className="account-main">
        <div className="crumbs"><Link to="/my-trips">My Trips</Link> <Icon name="right" size={12} /> <Link to={`${s.path}/bookings`}>{s.label}</Link> <Icon name="right" size={12} /> <span>{booking.id}</span></div>

        <div className="panel details-head">
          <div>
            <span className={`status status-${st}`}>{statusLabel[st]}</span>
            <h1>{booking.title}</h1>
            <span className="muted">{booking.subtitle}</span>
          </div>
          <div className="details-ids">
            <div><span className="muted small">Booking ID</span><b>{booking.id}</b></div>
            <div><span className="muted small">{refLabel}</span><b>{booking.ref}</b></div>
            <div><span className="muted small">Booked on</span><b>{fmtDate(booking.createdAt.slice(0, 10))}</b></div>
          </div>
        </div>

        {st === 'cancelled' && (
          <div className="notice notice-coral">
            <Icon name="close" size={18} />
            <div>
              <b>This booking was cancelled{booking.cancelledAt ? ` on ${fmtDate(booking.cancelledAt.slice(0, 10))}` : ''}.</b>
              <span>Refund of {formatCharge(booking.refund?.amount || 0)} {booking.refund?.amount ? 'will be credited to your original payment method within 5–7 working days.' : 'is not applicable.'} {booking.refund?.note}</span>
            </div>
          </div>
        )}
        {booking.sample && <div className="notice"><Icon name="file" size={18} /><span>This is a sample booking to show how your trips will appear.</span></div>}

        <div className="details-grid">
          <div className="details-main">
            <section className="panel"><Summary summary={booking.summary} /></section>
            <section className="panel">
              <h3>Traveller details</h3>
              <TravellerTable travellers={booking.travellers} />
            </section>
            <section className="panel">
              <h3>Contact details</h3>
              <div className="kv-grid">
                <div><span className="muted small">Email</span><b>{booking.contact.email}</b></div>
                <div><span className="muted small">Mobile</span><b>{booking.contact.phone}</b></div>
                {booking.contact.gstin && <div><span className="muted small">GSTIN</span><b>{booking.contact.gstin} · {booking.contact.company}</b></div>}
              </div>
            </section>
            {policy.length > 0 && (
              <section className="panel">
                <h3>Cancellation policy</h3>
                <ul className="policy-list">{policy.map((p) => <li key={p}>{p}</li>)}</ul>
              </section>
            )}
            {extraDetails}
            <AmendmentPanel booking={booking} />
          </div>

          <aside className="details-side">
            <section className="panel">
              <h3>{st === 'held' ? 'Supplier fare' : 'Payment summary'}</h3>
              <FareBreakdown fare={booking.fare} />
              <p className="muted small">{st === 'held' ? `Temporary hold with ${booking.supplierCode}. This booking is not ticketed.` : `Paid via ${payLabel[booking.payment]}`}</p>
            </section>
            <section className="panel confirm-actions">
              <h3>Manage booking</h3>
              <DocumentLink booking={booking} fallbackLabel={`Download ${module === 'insurance' ? 'policy' : 'e-ticket / voucher'}`} />
              <button type="button" className="btn btn-outline block" onClick={() => alert(`Invoice for ${booking.id} sent to ${booking.contact.email}`)}><Icon name="card" size={16} /> Email invoice</button>
              {st === 'upcoming' && allowCancel && <button type="button" className="btn btn-danger block" onClick={() => setConfirming(true)}>Cancel booking</button>}
              <a href="#support" className="btn btn-ghost block"><Icon name="headset" size={16} /> Contact support</a>
            </section>
          </aside>
        </div>
      </div>

      {confirming && estimate && (
        <div className="overlay" onClick={() => setConfirming(false)}>
          <div className="overlay-card modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <h3>Cancel this booking?</h3>
            <p className="muted">{booking.title} · {booking.id}</p>
            <div className="refund-box">
              <div className="summary-line"><span>Amount paid</span><span>{formatCharge(booking.fare.total)}</span></div>
              <div className="summary-line"><span>Cancellation charges</span><span>−{formatCharge(booking.fare.total - estimate.amount)}</span></div>
              <div className="summary-line total"><span>Estimated refund</span><b className="text-primary">{formatCharge(estimate.amount)}</b></div>
              <p className="muted small">{estimate.note}</p>
            </div>
            <div className="modal-actions">
              <button className="btn btn-outline" onClick={() => setConfirming(false)}>Keep booking</button>
              <button className="btn btn-danger" onClick={() => { cancelBooking(booking.id, estimate); setConfirming(false); window.scrollTo(0, 0); }}>Yes, cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
